import React, { useState, useEffect } from 'react';
import { ArrowLeft, Download, FileText, Layers } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../api/client';
import AdminLayout from '../../components/layout/AdminLayout';

const VaultExport = () => {
  const [data, setData] = useState(null);
  const [view, setView] = useState('pitch'); // 'pitch' or 'teaser'
  const navigate = useNavigate();

  useEffect(() => {
    apiClient.request('/api/vault/export/pitch-deck-data').then(setData).catch(() => {});
  }, []);

  const handlePrint = () => {
    window.print();
  };

  if (!data) return (
    <AdminLayout pageTitle="Pitch Deck Export">
      <div className="min-h-[40vh] flex items-center justify-center">
        <p className="text-gray-400">Loading export data...</p>
      </div>
    </AdminLayout>
  );

  return (
    <AdminLayout pageTitle="Pitch Deck Export">
      {/* Print-only style: hide admin shell chrome during print so the
          pitch-deck canvas exports cleanly. */}
      <style>{`
        @media print {
          aside, header, nav, .admin-sidebar { display: none !important; }
          body, html, .admin-content, main { background: #fff !important; }
          .vault-export-doc { padding: 0 !important; }
        }
      `}</style>

      {/* Controls (hidden in print) */}
      <div className="print:hidden bg-white border border-gray-200 rounded-xl px-5 py-3 mb-4 flex items-center justify-between shadow-sm" data-testid="export-controls">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/admin/vault')} className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900" data-testid="back-to-vault">
            <ArrowLeft size={16} /> Back to Vault
          </button>
          <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
            <button onClick={() => setView('pitch')} className={`px-4 py-1.5 rounded-md text-xs font-semibold transition-colors ${view === 'pitch' ? 'bg-[#7B4FD8] text-white' : 'text-gray-600'}`} data-testid="view-pitch">
              <Layers size={12} className="inline mr-1" /> Pitch Deck
            </button>
            <button onClick={() => setView('teaser')} className={`px-4 py-1.5 rounded-md text-xs font-semibold transition-colors ${view === 'teaser' ? 'bg-[#7B4FD8] text-white' : 'text-gray-600'}`} data-testid="view-teaser">
              <FileText size={12} className="inline mr-1" /> 2-Page Teaser
            </button>
          </div>
        </div>
        <button onClick={handlePrint} className="flex items-center gap-1.5 px-5 py-2 bg-[#1A1209] text-white text-sm font-semibold rounded-lg hover:bg-black transition-colors" data-testid="print-btn">
          <Download size={14} /> Export PDF
        </button>
      </div>

      {/* Document */}
      <div className="vault-export-doc flex justify-center py-2 print:py-0 print:block">
        {view === 'pitch' ? <PitchDeck data={data} /> : <TeaserDoc data={data} />}
      </div>
    </AdminLayout>
  );
};

// ─── PITCH DECK (1-PAGE) ─────────────────────────────────────────────────────

const PitchDeck = ({ data }) => {
  const m = data.metrics;
  const f = data.financials;
  const fr = data.fundraise;
  const v = data.valuation;
  const t = data.traction;

  return (
    <div className="bg-white w-[210mm] min-h-[297mm] shadow-2xl print:shadow-none print:w-full" style={{ fontFamily: "'Inter', 'Helvetica Neue', sans-serif" }} data-testid="pitch-deck">
      {/* Header */}
      <div className="bg-[#1A1209] text-white px-10 pt-8 pb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <img src={data.company.logo_url || "/hibff-logo.png"} alt="HiBFF" className="h-20 w-auto object-contain" data-testid="pitch-logo" />
            <div>
              <h1 className="text-2xl font-black tracking-tight">HiBFF <span className="text-[#F5831F] font-bold">— {data.company.tagline}</span></h1>
              <p className="text-[10px] text-gray-400 uppercase tracking-[0.2em]">Investor Summary</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-[10px] text-gray-500">Confidential</p>
            <p className="text-[10px] text-gray-500">{new Date().toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}</p>
          </div>
        </div>
        <p className="text-[11px] text-gray-300 italic leading-relaxed mb-4 max-w-2xl" data-testid="pitch-ethos">{data.company.ethos}</p>
        <p className="text-sm text-gray-300 max-w-xl leading-relaxed">{data.company.description}</p>
        <div className="flex gap-6 mt-4 text-[10px] text-gray-500">
          <span>Market: {data.company.market}</span>
          <span>Target: {data.company.target}</span>
          <span>hibff.com</span>
        </div>
      </div>

      <div className="px-10 py-6 space-y-5">
        {/* Key Metrics */}
        <div>
          <h2 className="text-[10px] font-bold text-[#7B4FD8] uppercase tracking-[0.15em] mb-3">Key Metrics</h2>
          <div className="grid grid-cols-6 gap-2">
            {[
              { label: 'MAU', value: m.mau?.toLocaleString(), color: '#3B82F6' },
              { label: 'MRR', value: `$${m.mrr?.toLocaleString()}`, color: '#10B981' },
              { label: 'ARR', value: `$${(m.arr / 1000).toFixed(0)}k`, color: '#10B981' },
              { label: 'Churn', value: `${m.churn_rate}%`, color: '#EF4444' },
              { label: 'LTV:CAC', value: `${m.ltv_cac_ratio}x`, color: '#8B5CF6' },
              { label: 'NPS', value: m.nps, color: '#F59E0B' },
            ].map(k => (
              <div key={k.label} className="bg-gray-50 rounded-lg p-3 text-center border border-gray-100">
                <p className="text-lg font-black text-[#1A1209]">{k.value}</p>
                <p className="text-[9px] text-gray-500 uppercase font-semibold">{k.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Financials + Revenue Chart */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <h2 className="text-[10px] font-bold text-[#7B4FD8] uppercase tracking-[0.15em] mb-3">Financials</h2>
            <div className="space-y-2">
              <div className="flex justify-between py-1.5 border-b border-gray-100"><span className="text-xs text-gray-500">Total Revenue</span><span className="text-xs font-bold text-[#1A1209]">${f.total_revenue?.toLocaleString()}</span></div>
              <div className="flex justify-between py-1.5 border-b border-gray-100"><span className="text-xs text-gray-500">Total Costs</span><span className="text-xs font-bold text-red-500">${f.total_costs?.toLocaleString()}</span></div>
              <div className="flex justify-between py-1.5 border-b border-gray-100"><span className="text-xs text-gray-500">Net P&L</span><span className={`text-xs font-bold ${f.net >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>${f.net?.toLocaleString()}</span></div>
              <div className="flex justify-between py-1.5 border-b border-gray-100"><span className="text-xs text-gray-500">Cash in Bank</span><span className="text-xs font-bold text-[#1A1209]">${(f.cash_in_bank / 1000).toFixed(0)}k</span></div>
              <div className="flex justify-between py-1.5"><span className="text-xs text-gray-500">Latest Month Revenue</span><span className="text-xs font-bold text-emerald-600">${f.latest_month_revenue?.toLocaleString()}</span></div>
            </div>
          </div>
          <div>
            <h2 className="text-[10px] font-bold text-[#7B4FD8] uppercase tracking-[0.15em] mb-3">Revenue Trajectory</h2>
            <div className="flex items-end gap-1 h-28 bg-gray-50 rounded-lg p-3 border border-gray-100">
              {f.revenue_trajectory?.map((r, i) => {
                const max = Math.max(...f.revenue_trajectory.map(x => Math.max(x.revenue, x.costs)), 1);
                return (
                  <div key={i} className="flex-1 flex flex-col items-center gap-0.5">
                    <div className="w-full flex gap-0.5 items-end h-16">
                      <div className="flex-1 bg-[#7B4FD8] rounded-t" style={{ height: `${(r.revenue / max) * 100}%`, minHeight: '2px' }} />
                      <div className="flex-1 bg-gray-300 rounded-t" style={{ height: `${(r.costs / max) * 100}%`, minHeight: '2px' }} />
                    </div>
                    <span className="text-[7px] text-gray-400">{r.month?.split(' ')[0]?.slice(0, 3)}</span>
                  </div>
                );
              })}
            </div>
            <div className="flex gap-4 mt-1.5 text-[8px] text-gray-400">
              <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 bg-[#7B4FD8] rounded-full" /> Revenue</span>
              <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 bg-gray-300 rounded-full" /> Costs</span>
            </div>
          </div>
        </div>

        {/* Fundraise + Valuation */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <h2 className="text-[10px] font-bold text-[#7B4FD8] uppercase tracking-[0.15em] mb-3">Capital & Valuation</h2>
            <div className="bg-[#1A1209] rounded-xl p-4 text-white">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><p className="text-[9px] text-gray-500 uppercase">Valuation</p><p className="text-lg font-black">${v.current > 0 ? `${(v.current / 1000000).toFixed(1)}M` : 'N/A'}</p></div>
                <div><p className="text-[9px] text-gray-500 uppercase">Total Raised</p><p className="text-lg font-black">${(fr.total_raised / 1000).toFixed(0)}k</p></div>
                <div><p className="text-[9px] text-gray-500 uppercase">Active Investors</p><p className="text-lg font-black">{fr.active_investors}</p></div>
                <div><p className="text-[9px] text-gray-500 uppercase">Founder Stake</p><p className="text-lg font-black">{fr.founder_pct}%</p></div>
              </div>
              {fr.closed_rounds?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-700">
                  <p className="text-[9px] text-gray-500 uppercase mb-1">Rounds Closed</p>
                  {fr.closed_rounds.map((r, i) => (
                    <div key={i} className="flex justify-between text-[10px] py-0.5">
                      <span className="text-gray-400">{r.name}</span>
                      <span className="text-white font-semibold">${(r.amount / 1000).toFixed(0)}k @ ${(r.valuation / 1000000).toFixed(1)}M</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div>
            <h2 className="text-[10px] font-bold text-[#7B4FD8] uppercase tracking-[0.15em] mb-3">Traction & Milestones</h2>
            <div className="space-y-2">
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                <p className="text-xs font-bold text-[#1A1209]">{t.milestones_achieved} / {t.milestones_total} milestones achieved</p>
                <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1.5">
                  <div className="bg-[#F5831F] h-full rounded-full" style={{ width: `${(t.milestones_achieved / Math.max(t.milestones_total, 1)) * 100}%` }} />
                </div>
              </div>
              {t.recent_milestones?.slice(0, 4).map((ms, i) => (
                <div key={i} className="flex items-center gap-2 text-[10px]">
                  <span className="w-1.5 h-1.5 bg-[#F5831F] rounded-full flex-shrink-0" />
                  <span className="text-gray-700 flex-1">{ms.title}</span>
                  <span className="text-gray-400">{ms.date}</span>
                </div>
              ))}
              {t.highest_offer && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 mt-2">
                  <p className="text-[9px] text-amber-600 uppercase font-bold">Highest Offer Received</p>
                  <p className="text-sm font-black text-[#1A1209]">${(t.highest_offer.amount / 1000000).toFixed(1)}M <span className="text-[10px] font-normal text-gray-500">from {t.highest_offer.buyer}</span></p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Next Round */}
        {fr.next_round && (
          <div className="bg-[#7B4FD8] rounded-xl p-4 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[9px] uppercase tracking-wider text-purple-200">Seeking: {fr.next_round.round_name}</p>
                <p className="text-2xl font-black mt-0.5">${(fr.next_round.target_raise / 1000000).toFixed(0)}M <span className="text-sm font-normal text-purple-200">@ ${(fr.next_round.pre_money_valuation / 1000000).toFixed(0)}M pre-money</span></p>
              </div>
              <div className="text-right">
                <p className="text-[10px] text-purple-200">Target close: {fr.next_round.target_close_date || 'TBD'}</p>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between pt-3 border-t border-gray-200">
          <p className="text-[9px] text-gray-400">This document is confidential and intended solely for the named recipient.</p>
          <p className="text-[9px] text-gray-400">hibff.com — {new Date().getFullYear()}</p>
        </div>
      </div>
    </div>
  );
};

// ─── TEASER DOCUMENT (2-PAGE) ────────────────────────────────────────────────

const TeaserDoc = ({ data }) => {
  const m = data.metrics;
  const f = data.financials;
  const fr = data.fundraise;
  const v = data.valuation;
  const t = data.traction;
  const ipo = data.ipo_readiness;

  return (
    <div data-testid="teaser-doc">
      {/* PAGE 1 */}
      <div className="bg-white w-[210mm] min-h-[297mm] shadow-2xl print:shadow-none print:w-full mb-8 print:mb-0" style={{ fontFamily: "'Inter', 'Helvetica Neue', sans-serif", pageBreakAfter: 'always' }}>
        {/* Hero Header */}
        <div className="bg-[#1A1209] text-white px-10 pt-10 pb-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <img src={data.company.logo_url || "/hibff-logo.png"} alt="HiBFF" className="h-24 w-auto object-contain" data-testid="teaser-logo" />
              <div>
                <h1 className="text-3xl font-black tracking-tight">HiBFF <span className="text-[#F5831F] font-bold text-2xl">— {data.company.tagline}</span></h1>
                <p className="text-xs text-[#F5831F] font-semibold tracking-wider">SOCIAL INTELLIGENCE PLATFORM</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-[10px] text-gray-500 uppercase">Investment Teaser</p>
              <p className="text-[10px] text-gray-500">{new Date().toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}</p>
              <p className="text-[10px] text-gray-600 mt-0.5">Strictly Confidential</p>
            </div>
          </div>
          <p className="text-xs text-gray-300 italic leading-relaxed mb-5 max-w-2xl" data-testid="teaser-ethos">{data.company.ethos}</p>
          <h2 className="text-xl font-bold text-white leading-snug max-w-lg mb-3">
            Building the world&apos;s leading platform for <span className="text-[#F5831F]">social skills development</span>
          </h2>
          <p className="text-sm text-gray-400 leading-relaxed max-w-xl">{data.company.description}</p>
        </div>

        <div className="px-10 py-6 space-y-5">
          {/* Headline Metrics */}
          <div>
            <h2 className="text-[10px] font-bold text-[#7B4FD8] uppercase tracking-[0.15em] mb-3 flex items-center gap-2">
              <span className="w-4 h-[2px] bg-[#7B4FD8]" /> Performance Snapshot
            </h2>
            <div className="grid grid-cols-4 gap-3">
              {[
                { label: 'Monthly Active Users', value: m.mau?.toLocaleString(), sub: 'and growing', accent: '#3B82F6' },
                { label: 'Monthly Recurring Revenue', value: `$${m.mrr?.toLocaleString()}`, sub: `$${(m.arr / 1000).toFixed(0)}k ARR`, accent: '#10B981' },
                { label: 'LTV : CAC Ratio', value: `${m.ltv_cac_ratio}x`, sub: m.ltv_cac_ratio >= 3 ? 'Strong unit economics' : 'Improving', accent: '#8B5CF6' },
                { label: 'Net Promoter Score', value: m.nps, sub: m.nps >= 50 ? 'Excellent' : 'Good', accent: '#F59E0B' },
              ].map(k => (
                <div key={k.label} className="border-l-[3px] pl-3 py-1" style={{ borderColor: k.accent }}>
                  <p className="text-2xl font-black text-[#1A1209]">{k.value}</p>
                  <p className="text-[9px] text-gray-500 uppercase font-semibold">{k.label}</p>
                  <p className="text-[8px] text-gray-400 mt-0.5">{k.sub}</p>
                </div>
              ))}
            </div>
          </div>

          {/* The Problem + Solution */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="bg-gray-50 rounded-xl p-5 border border-gray-100">
              <h3 className="text-xs font-black text-[#1A1209] mb-2 flex items-center gap-2">
                <span className="w-2 h-2 bg-red-400 rounded-full" /> The Problem
              </h3>
              <ul className="space-y-1.5 text-[10px] text-gray-600 leading-relaxed">
                {(data.narrative?.problem || []).map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            </div>
            <div className="bg-[#7B4FD8]/5 rounded-xl p-5 border border-[#7B4FD8]/20">
              <h3 className="text-xs font-black text-[#1A1209] mb-2 flex items-center gap-2">
                <span className="w-2 h-2 bg-[#7B4FD8] rounded-full" /> Our Solution
              </h3>
              <ul className="space-y-1.5 text-[10px] text-gray-600 leading-relaxed">
                {(data.narrative?.solution || []).map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            </div>
          </div>

          {/* Revenue Growth Chart */}
          <div>
            <h2 className="text-[10px] font-bold text-[#7B4FD8] uppercase tracking-[0.15em] mb-3 flex items-center gap-2">
              <span className="w-4 h-[2px] bg-[#7B4FD8]" /> Revenue Growth
            </h2>
            <div className="bg-gray-50 rounded-xl p-5 border border-gray-100">
              <div className="flex items-end gap-1.5 h-28">
                {f.revenue_trajectory?.map((r, i) => {
                  const max = Math.max(...f.revenue_trajectory.map(x => x.revenue), 1);
                  return (
                    <div key={i} className="flex-1 flex flex-col items-center gap-1">
                      <p className="text-[7px] text-gray-500 font-bold">${(r.revenue / 1000).toFixed(0)}k</p>
                      <div className="w-full bg-gradient-to-t from-[#7B4FD8] to-[#A78BFA] rounded-t-md transition-all" style={{ height: `${(r.revenue / max) * 80}%`, minHeight: '4px' }} />
                      <p className="text-[8px] text-gray-400 font-semibold">{r.month?.split(' ')[0]?.slice(0, 3)}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Capital Structure */}
          <div>
            <h2 className="text-[10px] font-bold text-[#7B4FD8] uppercase tracking-[0.15em] mb-3 flex items-center gap-2">
              <span className="w-4 h-[2px] bg-[#7B4FD8]" /> Capital Structure
            </h2>
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-[#1A1209] rounded-xl p-4 text-white text-center">
                <p className="text-2xl font-black">${v.current > 0 ? `${(v.current / 1000000).toFixed(1)}M` : 'N/A'}</p>
                <p className="text-[9px] text-gray-500 uppercase">Current Valuation</p>
              </div>
              <div className="bg-[#1A1209] rounded-xl p-4 text-white text-center">
                <p className="text-2xl font-black">${(fr.total_raised / 1000).toFixed(0)}k</p>
                <p className="text-[9px] text-gray-500 uppercase">Total Raised</p>
              </div>
              <div className="bg-[#1A1209] rounded-xl p-4 text-white text-center">
                <p className="text-2xl font-black">{fr.founder_pct}%</p>
                <p className="text-[9px] text-gray-500 uppercase">Founder Equity</p>
              </div>
            </div>
          </div>

          {/* Footer Page 1 */}
          <div className="flex items-center justify-between pt-3 border-t border-gray-200">
            <p className="text-[9px] text-gray-400">HiBFF Ltd. — Strictly Confidential</p>
            <p className="text-[9px] text-gray-400">Page 1 of 2</p>
          </div>
        </div>
      </div>

      {/* PAGE 2 */}
      <div className="bg-white w-[210mm] min-h-[297mm] shadow-2xl print:shadow-none print:w-full" style={{ fontFamily: "'Inter', 'Helvetica Neue', sans-serif" }}>
        {/* Mini Header */}
        <div className="bg-[#1A1209] px-10 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={data.company.logo_url || "/hibff-logo.png"} alt="HiBFF" className="h-12 w-auto object-contain" data-testid="teaser-mini-logo" />
            <span className="text-sm font-bold text-white">HiBFF</span>
            <span className="text-[10px] text-[#F5831F]">— {data.company.tagline}</span>
            <span className="text-[10px] text-gray-500">— Investment Teaser (cont.)</span>
          </div>
          <span className="text-[10px] text-gray-500">{new Date().toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}</span>
        </div>

        <div className="px-10 py-6 space-y-5">
          {/* Unit Economics Deep Dive */}
          <div>
            <h2 className="text-[10px] font-bold text-[#7B4FD8] uppercase tracking-[0.15em] mb-3 flex items-center gap-2">
              <span className="w-4 h-[2px] bg-[#7B4FD8]" /> Unit Economics
            </h2>
            <div className="grid grid-cols-5 gap-2">
              {[
                { label: 'CAC', value: `$${m.cac}`, desc: 'Cost to acquire' },
                { label: 'LTV', value: `$${m.ltv}`, desc: 'Lifetime value' },
                { label: 'LTV:CAC', value: `${m.ltv_cac_ratio}x`, desc: 'Ratio' },
                { label: 'Churn', value: `${m.churn_rate}%`, desc: 'Monthly' },
                { label: 'Payback', value: m.cac > 0 ? `${Math.ceil(m.cac / Math.max(m.mrr / Math.max(m.mau, 1), 1))}mo` : 'N/A', desc: 'CAC payback' },
              ].map(k => (
                <div key={k.label} className="bg-gray-50 rounded-lg p-3 text-center border border-gray-100">
                  <p className="text-lg font-black text-[#1A1209]">{k.value}</p>
                  <p className="text-[9px] text-gray-500 uppercase font-semibold">{k.label}</p>
                  <p className="text-[7px] text-gray-400">{k.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Market + Business Model */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <h2 className="text-[10px] font-bold text-[#7B4FD8] uppercase tracking-[0.15em] mb-3 flex items-center gap-2">
                <span className="w-4 h-[2px] bg-[#7B4FD8]" /> Target Market
              </h2>
              <div className="space-y-2">
                {(data.market_segments || []).map(s => (
                  <div key={s.seg} className="flex items-center gap-3 bg-gray-50 rounded-lg p-3 border border-gray-100">
                    <div className="w-10 h-10 rounded-lg bg-[#7B4FD8]/10 flex items-center justify-center">
                      <span className="text-xs font-black text-[#7B4FD8]">{s.seg}</span>
                    </div>
                    <div>
                      <p className="text-sm font-black text-[#1A1209]">{s.val}</p>
                      <p className="text-[9px] text-gray-500">{s.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h2 className="text-[10px] font-bold text-[#7B4FD8] uppercase tracking-[0.15em] mb-3 flex items-center gap-2">
                <span className="w-4 h-[2px] bg-[#7B4FD8]" /> Revenue Streams
              </h2>
              <div className="space-y-2">
                {(data.revenue_streams || []).map(s => (
                  <div key={s.stream} className="flex items-center justify-between bg-gray-50 rounded-lg p-3 border border-gray-100">
                    <div>
                      <p className="text-xs font-bold text-[#1A1209]">{s.stream}</p>
                      <p className="text-[9px] text-gray-500">{s.desc}</p>
                    </div>
                    <span className="text-sm font-black text-[#7B4FD8]">{s.pct}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Fundraise & Milestones */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <h2 className="text-[10px] font-bold text-[#7B4FD8] uppercase tracking-[0.15em] mb-3 flex items-center gap-2">
                <span className="w-4 h-[2px] bg-[#7B4FD8]" /> Fundraise History
              </h2>
              {fr.closed_rounds?.length > 0 && (
                <div className="space-y-1.5">
                  {fr.closed_rounds.map((r, i) => (
                    <div key={i} className="flex items-center justify-between bg-gray-50 rounded-lg p-3 border border-gray-100">
                      <span className="text-xs font-bold text-[#1A1209]">{r.name}</span>
                      <div className="text-right">
                        <p className="text-xs font-black text-[#1A1209]">${(r.amount / 1000).toFixed(0)}k</p>
                        <p className="text-[8px] text-gray-400">${(r.valuation / 1000000).toFixed(1)}M post</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {fr.next_round && (
                <div className="bg-[#F5831F] rounded-xl p-4 text-white mt-3">
                  <p className="text-[9px] uppercase tracking-wider text-orange-200 font-bold">Now Raising</p>
                  <p className="text-xl font-black mt-0.5">{fr.next_round.round_name}: ${(fr.next_round.target_raise / 1000000).toFixed(0)}M</p>
                  <p className="text-[10px] text-orange-200 mt-0.5">${(fr.next_round.pre_money_valuation / 1000000).toFixed(0)}M pre-money valuation</p>
                </div>
              )}
            </div>
            <div>
              <h2 className="text-[10px] font-bold text-[#7B4FD8] uppercase tracking-[0.15em] mb-3 flex items-center gap-2">
                <span className="w-4 h-[2px] bg-[#7B4FD8]" /> Key Milestones
              </h2>
              <div className="space-y-1.5">
                {t.recent_milestones?.map((ms, i) => (
                  <div key={i} className="flex items-center gap-2 p-2">
                    <span className="w-2 h-2 bg-[#F5831F] rounded-full flex-shrink-0" />
                    <span className="text-[10px] text-gray-700 flex-1">{ms.title}</span>
                    <span className="text-[9px] text-gray-400">{ms.date}</span>
                  </div>
                ))}
              </div>
              {t.pr_reach > 0 && (
                <div className="bg-gray-50 rounded-lg p-3 border border-gray-100 mt-3">
                  <p className="text-[9px] text-gray-500 uppercase font-bold">Media & PR Reach</p>
                  <p className="text-lg font-black text-[#1A1209]">{(t.pr_reach / 1000).toFixed(0)}k+ <span className="text-[10px] font-normal text-gray-500">people reached across {t.pr_count} activities</span></p>
                </div>
              )}
            </div>
          </div>

          {/* NASDAQ Path */}
          <div className="bg-gradient-to-r from-indigo-900 to-purple-900 rounded-xl p-5 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[9px] uppercase tracking-wider text-indigo-300 font-bold">Path to Public Markets</p>
                <p className="text-lg font-black mt-0.5">NASDAQ Listing Readiness: {ipo.readiness_pct}%</p>
                <p className="text-[10px] text-indigo-300 mt-0.5">{ipo.nasdaq_met} of {ipo.nasdaq_total} requirements met — {ipo.board_members} board members ({ipo.independent_directors} independent)</p>
              </div>
              <div className="w-16 h-16 relative">
                <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
                  <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="3" />
                  <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="#F5831F" strokeWidth="3" strokeDasharray={`${ipo.readiness_pct}, 100`} strokeLinecap="round" />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-xs font-black">{ipo.readiness_pct}%</span>
                </div>
              </div>
            </div>
          </div>

          {/* Contact */}
          <div className="bg-[#1A1209] rounded-xl p-5 flex items-center justify-between">
            <div>
              <p className="text-sm font-black text-white">Interested in HiBFF?</p>
              <p className="text-[10px] text-gray-500 mt-0.5">For investment enquiries, contact the founder directly.</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-[#F5831F] font-bold">robert@hibff.com</p>
              <p className="text-[10px] text-gray-500">hibff.com</p>
            </div>
          </div>

          {/* Footer Page 2 */}
          <div className="flex items-center justify-between pt-3 border-t border-gray-200">
            <p className="text-[8px] text-gray-400">This document is confidential and intended solely for the named recipient. It does not constitute an offer of securities.</p>
            <p className="text-[9px] text-gray-400">Page 2 of 2</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VaultExport;
